import React, { ReactElement, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { ReactState } from '../../Types/Types'
import Button from '../Button'
import Checkbox from '../Checkbox'
import { Props as CheckboxProps } from '../Checkbox/Checkbox'
import {
    RoundedButton,
    RoundedButtonProps
} from '../../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { RouterCheckbox } from '../../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { LanguageUtils } from '../../Utils/LanguageUtils'

/**
 * Component properties type.
 */
export type InlinePromptProps = {
    message: string | ReactElement
    onConfirm?: (messageChecked?: boolean) => void
    confirmButtonText: string
    onCancel?: () => void
    cancelButtonText: string
    defaultChecked?: boolean
    checkMessage?: string | ReactElement
    infoMode?: boolean
    isRouterMode?: boolean
}

/**
 * Inline configurable generic prompt component.
 *
 * @param message Message to be displayed.
 * @param messageRenderElement Element to be rendered instead of message, if provided.
 * @param onConfirm Callback on confirm.
 * @param confirmButtonText Confirmation button text.
 * @param onCancel Callback on cancel.
 * @param cancelButtonText Cancel button text.
 */
const InlinePrompt: React.FC<InlinePromptProps> = ({
    message,
    onConfirm,
    confirmButtonText,
    onCancel,
    cancelButtonText,
    defaultChecked = false,
    checkMessage,
    infoMode = false,
    isRouterMode
}: InlinePromptProps): ReactElement => {
    const [messageChecked, setMessageChecked]: ReactState<boolean> = useState<boolean>(defaultChecked)

    const renderButton = (buttonProps: RoundedButtonProps): ReactElement =>
        isRouterMode ? <RoundedButton {...buttonProps} /> : <Button {...buttonProps} />

    const renderCheckbox = (checkboxProps: CheckboxProps): ReactElement =>
        isRouterMode ? <RouterCheckbox {...checkboxProps} /> : <Checkbox {...checkboxProps} />

    useHotkeys('enter', (): void => onConfirm?.(messageChecked))
    useHotkeys('esc', (e: Event): void => {
        e?.preventDefault()
        onCancel?.()
    })

    return (
        <div className={`inline-prompt ${isRouterMode ? 'ip-router-mode' : EMPTY_STRING}`}>
            <div className='message'>
                {typeof message === 'string' ? LanguageUtils.formatLanguageEntry(message) : message}
            </div>
            {checkMessage ? (
                <div className='check-message'>
                    {renderCheckbox({
                        isChecked: messageChecked,
                        onChange: setMessageChecked,
                        labelRight: checkMessage
                    })}
                </div>
            ) : (
                <></>
            )}
            <div className='actions'>
                {onCancel ? (
                    renderButton({
                        onClick: onCancel,
                        children: cancelButtonText,
                        isOutlined: true
                    })
                ) : (
                    <></>
                )}
                {!infoMode ? (
                    renderButton({
                        onClick: (): void => onConfirm && onConfirm(messageChecked),
                        children: confirmButtonText,
                        isOutlined: false
                    })
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}

export default InlinePrompt
